import { User, ShowingPageType } from '../types';

/** Mock formation hub cards — shelved; Je finance count comes from API only. */
const FORMATION_FUNDER_HUB_COUNT = 0;

export type ContextOrgType = 'school' | 'company' | 'teacher' | 'user' | 'formation';

const CONTEXT_ORG_TYPES: ReadonlySet<string> = new Set([
  'school',
  'company',
  'teacher',
  'user',
  'formation',
]);

/** Parse dashboard `?ctx=company-20` (type + id; id may contain dashes). */
export function parseCtxParam(
  ctx: string | null | undefined
): { id: string; type: ContextOrgType } | null {
  if (!ctx) return null;
  const dash = ctx.indexOf('-');
  if (dash <= 0) return null;
  const type = ctx.slice(0, dash);
  const id = ctx.slice(dash + 1);
  if (!id || !CONTEXT_ORG_TYPES.has(type)) return null;
  return { id, type: type as ContextOrgType };
}

/**
 * Org highlighted in the switcher: prefer `?ctx=` (updates on same-type switches),
 * then localStorage, then page-type defaults.
 */
export function resolveSwitcherContext(options: {
  search: string;
  showingPageType: ShowingPageType;
  formationOrgFallbackId?: string | number | null;
}): { id: string; type: ContextOrgType } | null {
  const fromCtx = parseCtxParam(new URLSearchParams(options.search).get('ctx'));
  if (fromCtx) return fromCtx;

  const savedContextId = localStorage.getItem('selectedContextId');
  const savedContextType = localStorage.getItem('selectedContextType') as ContextOrgType | null;
  if (savedContextId && savedContextType) {
    return { id: savedContextId, type: savedContextType };
  }

  if (options.showingPageType === 'teacher') {
    return { id: 'teacher-dashboard', type: 'teacher' };
  }
  if (options.showingPageType === 'user') {
    return { id: 'user-dashboard', type: 'user' };
  }
  if (options.showingPageType === 'of') {
    const savedId = localStorage.getItem('selectedContextId');
    const fallback = options.formationOrgFallbackId;
    return {
      id: String(savedId || fallback || 'formation'),
      type: 'formation',
    };
  }

  return null;
}

/**
 * Get the selected organization ID from localStorage
 * Validates that user still has admin/superadmin access
 * Falls back to first organization if no selection or invalid
 */
export const getSelectedOrganizationId = (
  user: User,
  showingPageType: ShowingPageType
): number | undefined => {
  const savedContextId = localStorage.getItem('selectedContextId');
  const savedContextType = localStorage.getItem('selectedContextType') as 'school' | 'company' | 'teacher' | 'user' | 'formation' | null;
  
  // If we have a saved context and it matches the current page type
  if (savedContextId && savedContextType) {
    if (savedContextType === 'company' && showingPageType === 'pro') {
      const company = user.available_contexts?.companies?.find(
        (c: any) => c.id.toString() === savedContextId && (c.role === 'admin' || c.role === 'superadmin')
      );
      if (company) return Number(savedContextId);
    } else if (savedContextType === 'school' && (showingPageType === 'edu' || showingPageType === 'teacher')) {
      // For teachers: check if confirmed member (any role), not just admin/superadmin
      // For edu: still require admin/superadmin
      const school = user.available_contexts?.schools?.find(
        (s: any) => s.id.toString() === savedContextId
      );
      if (school) {
        if (showingPageType === 'teacher' || school.role === 'admin' || school.role === 'superadmin') {
          return Number(savedContextId);
        }
      }
    } else if (savedContextType === 'formation' && showingPageType === 'of') {
      const ofOrg = user.available_contexts?.formation_organizations?.find(
        (o: any) => o.id.toString() === savedContextId && (o.role === 'admin' || o.role === 'superadmin')
      );
      if (ofOrg) return Number(savedContextId);
    }
  }
  
  // Fallback to first organization (backward compatibility)
  if (showingPageType === 'pro') {
    return user.available_contexts?.companies?.[0]?.id;
  } else if (showingPageType === 'edu') {
    return user.available_contexts?.schools?.[0]?.id;
  } else if (showingPageType === 'teacher') {
    // For teachers: return first confirmed school membership (any role)
    return user.available_contexts?.schools?.[0]?.id;
  } else if (showingPageType === 'of') {
    return user.available_contexts?.formation_organizations?.[0]?.id;
  }
  
  return undefined;
};

/**
 * Get selected school ID (for school/teacher contexts)
 */
export const getSelectedSchoolId = (user: User, showingPageType: ShowingPageType): number | null => {
  const orgId = getSelectedOrganizationId(user, showingPageType);
  if (showingPageType === 'edu' || showingPageType === 'teacher') {
    return orgId || null;
  }
  return null;
};

/**
 * Get selected company ID (for company contexts)
 */
export const getSelectedCompanyId = (user: User, showingPageType: ShowingPageType): number | null => {
  const orgId = getSelectedOrganizationId(user, showingPageType);
  if (showingPageType === 'pro') {
    return orgId || null;
  }
  return null;
};

/**
 * Get selected organization role
 */
export const getSelectedOrganizationRole = (user: User, showingPageType: ShowingPageType): string => {
  const savedContextId = localStorage.getItem('selectedContextId');
  const savedContextType = localStorage.getItem('selectedContextType') as 'school' | 'company' | null;
  
  if (savedContextId && savedContextType) {
    if (savedContextType === 'company' && showingPageType === 'pro') {
      const company = user.available_contexts?.companies?.find(
        (c: any) => c.id.toString() === savedContextId
      );
      return company?.role || '';
    } else if (savedContextType === 'school' && (showingPageType === 'edu' || showingPageType === 'teacher')) {
      const school = user.available_contexts?.schools?.find(
        (s: any) => s.id.toString() === savedContextId
      );
      return school?.role || '';
    }
  }
  
  // Fallback
  if (showingPageType === 'pro') {
    return user.available_contexts?.companies?.[0]?.role || '';
  } else if (showingPageType === 'edu' || showingPageType === 'teacher') {
    return user.available_contexts?.schools?.[0]?.role || '';
  }
  
  return '';
};

/** Projects this org finances (designation). 0 = the Je finance tab must not exist. */
export const getFinancedProjectsCount = (
  user: User,
  showingPageType: ShowingPageType
): number => {
  if (showingPageType !== 'pro') return 0;
  const orgId = getSelectedOrganizationId(user, showingPageType);
  const company = user.available_contexts?.companies?.find(
    (c) => Number(c.id) === Number(orgId)
  );
  const apiCount = Number(company?.financed_projects_count || 0);
  return Math.max(apiCount, FORMATION_FUNDER_HUB_COUNT);
};

export const jeFinanceLabel = (count: number): string => `Je finance (${count})`;

/** Surfaces where the funder stays in the company (Pro) space — do not steal OF. */
export const isFunderAppPath = (pathname: string, search = ''): boolean =>
  pathname.startsWith('/follow/') ||
  pathname.startsWith('/funded-projects') ||
  pathname === '/financeur' ||
  (pathname.startsWith('/projects') && new URLSearchParams(search).get('tab') === 'je-finance');

export const isAuthenticatedSession = (): boolean => Boolean(localStorage.getItem('jwt_token')?.trim());

export const governableCompanies = (user: User) =>
  (user.available_contexts?.companies || []).filter(
    (company) => company.role === 'admin' || company.role === 'superadmin'
  );

export const applyCompanySpaceContext = (companyId: number): void => {
  localStorage.setItem('selectedPageType', 'pro');
  localStorage.setItem('selectedContextId', String(companyId));
  localStorage.setItem('selectedContextType', 'company');
};

export const isCompanyGovernContext = (user: User, showingPageType: ShowingPageType): boolean => {
  if (showingPageType !== 'pro') return false;
  const orgId = getSelectedOrganizationId(user, showingPageType);
  return Boolean(orgId) && governableCompanies(user).some((company) => Number(company.id) === Number(orgId));
};

export const currentUserIsDesignatedFunder = (
  user: User,
  funder?: {
    funderEmail?: string | null;
    funderUserId?: number | null;
    funderCompanyId?: number | null;
    viewerIsFunder?: boolean | null;
  } | null
): boolean => {
  if (!funder) return false;
  if (funder.viewerIsFunder === true) return true;
  if (funder.viewerIsFunder === false) return false;
  const email = user.email?.trim().toLowerCase();
  if (email && funder.funderEmail?.trim().toLowerCase() === email) return true;
  if (funder.funderUserId != null && String(user.id) === String(funder.funderUserId)) return true;
  if (funder.funderCompanyId != null) {
    return governableCompanies(user).some((company) => Number(company.id) === Number(funder.funderCompanyId));
  }
  return false;
};
